/**
 * LoginMethodTabs — email/password login form.
 *
 * The component name is kept for existing imports, but the supported first
 * factor is intentionally limited to email/password.
 */

import { useState, type ReactNode } from 'react';
import { Smartphone } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

interface LoginMethodTabsProps {
  onError?: (message: string) => void;
  /** true from submit until sign-in, including any TOTP step, has finished */
  onSigningInChange?: (signingIn: boolean) => void;
  forgotPasswordSlot?: ReactNode;
}

const FIELD_LABEL =
  'font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-ink-2';

const AUTH_INPUT =
  'h-12 rounded-[10px] border border-warm-border bg-paper px-[14px] text-[15px] text-ink shadow-none placeholder:text-ink-3 transition-colors focus-visible:border-coral focus-visible:ring-[3px] focus-visible:ring-coral/15 focus-visible:ring-offset-0';

const AUTH_INPUT_ERROR =
  'h-12 rounded-[10px] border border-[#C8553D] bg-paper px-[14px] text-[15px] text-ink shadow-none placeholder:text-ink-3 transition-colors focus-visible:border-[#C8553D] focus-visible:ring-[3px] focus-visible:ring-[#C8553D]/15 focus-visible:ring-offset-0';

const PRIMARY_BUTTON =
  'h-12 w-full rounded-[10px] border border-coral-dark bg-coral text-[15px] font-medium text-white shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,_0_1px_1px_rgba(154,78,58,0.25)] transition-colors hover:bg-coral-hover disabled:cursor-not-allowed disabled:opacity-90';

export function LoginMethodTabs({
  onError,
  onSigningInChange,
  forgotPasswordSlot,
}: LoginMethodTabsProps) {
  return (
    <PasswordForm
      onError={onError}
      onSigningInChange={onSigningInChange}
      forgotPasswordSlot={forgotPasswordSlot}
    />
  );
}

function PasswordForm({
  onError,
  onSigningInChange,
  forgotPasswordSlot,
}: {
  onError?: (msg: string) => void;
  onSigningInChange?: (signingIn: boolean) => void;
  forgotPasswordSlot?: ReactNode;
}) {
  const { signIn, signOut, getMFALevel, getMFAFactors, verifyMFALogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    onSigningInChange?.(true);
    let awaitingMfa = false;
    try {
      const { error } = await signIn(email, password);
      if (error) {
        onError?.(error.message || 'Invalid email or password');
        return;
      }

      const { nextLevel } = await getMFALevel();
      if (nextLevel === 'aal2') {
        const factors = await getMFAFactors();
        const verified = factors.find((f) => f.status === 'verified');
        if (verified) {
          setMfaFactorId(verified.id);
          setMfaRequired(true);
          awaitingMfa = true;
        }
      }
    } finally {
      setIsLoading(false);
      if (!awaitingMfa) onSigningInChange?.(false);
    }
  };

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mfaCode.length !== 6) return;
    setIsLoading(true);
    setMfaError('');
    try {
      const { error } = await verifyMFALogin(mfaFactorId, mfaCode);
      if (error) {
        setMfaError(error.message || 'Invalid code. Try again.');
        setMfaCode('');
      } else {
        onSigningInChange?.(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (mfaRequired) {
    return (
      <form onSubmit={handleMFASubmit} className="flex flex-col gap-[14px]">
        <div className="flex items-start gap-3 rounded-[10px] border border-teal-dark/25 bg-teal-light px-4 py-[14px]">
          <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-teal-dark" />
          <p className="text-[13px] leading-[1.45] text-ink-2">
            Enter the 6-digit code from your authenticator app.
          </p>
        </div>
        <label htmlFor="mfa-login-code" className="flex flex-col gap-1.5">
          <span className={FIELD_LABEL}>Authenticator code</span>
          <Input
            id="mfa-login-code"
            type="text"
            inputMode="numeric"
            placeholder="123456"
            value={mfaCode}
            onChange={(e) => {
              setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              setMfaError('');
            }}
            maxLength={6}
            className={`${mfaError ? AUTH_INPUT_ERROR : AUTH_INPUT} text-center font-mono tracking-[0.4em]`}
            autoFocus
            autoComplete="one-time-code"
          />
          {mfaError && (
            <span className="text-[12.5px] text-[#C8553D]">{mfaError}</span>
          )}
        </label>
        <Button
          type="submit"
          className={PRIMARY_BUTTON}
          disabled={mfaCode.length !== 6 || isLoading}
        >
          {isLoading ? 'Verifying…' : 'Verify and continue'}
        </Button>
        <button
          type="button"
          onClick={async () => {
            // The password step already created an aal1 session; drop it so
            // going back cannot skip the TOTP step. Keep signingIn true until
            // signOut resolves, else LoginPage redirects with the aal1 session.
            setMfaRequired(false);
            setMfaCode('');
            setMfaError('');
            try {
              await signOut();
            } catch {
              // fall through; finally clears the flag so the user is not stuck
            } finally {
              onSigningInChange?.(false);
            }
          }}
          className="self-center text-[13px] text-ink-2 hover:text-ink"
        >
          ← Back to sign in
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-[14px]">
      <label htmlFor="login-email" className="flex flex-col gap-1.5">
        <span className={FIELD_LABEL}>Email</span>
        <Input
          id="login-email"
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          className={AUTH_INPUT}
          required
        />
      </label>
      <label htmlFor="login-password" className="flex flex-col gap-1.5">
        <span className={FIELD_LABEL}>Password</span>
        <Input
          id="login-password"
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          className={AUTH_INPUT}
          required
        />
      </label>
      {forgotPasswordSlot}
      <Button type="submit" className={PRIMARY_BUTTON} disabled={isLoading}>
        {isLoading ? (
          <span className="inline-flex items-center gap-2.5">
            <ButtonSpinner />
            Signing in…
          </span>
        ) : (
          'Sign in'
        )}
      </Button>
    </form>
  );
}

function ButtonSpinner() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="animate-spin"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="#FFFFFF"
        strokeOpacity="0.25"
        strokeWidth="2.5"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="#FFFFFF"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
